import { useCallback, useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatErr, usePoll } from "@/lib/api";
import { toast } from "sonner";
import { RequirementRow } from "@/components/funding/RequirementRow";
import { GapPlan } from "@/components/funding/GapPlan";
import { FundingReadyBadge } from "@/components/funding/FundingReadyBadge";
import { FundingShare } from "@/components/funding/FundingShare";
import { ScanDialog } from "@/components/ScanDialog";
import { EXPIRING, ExpiryDialog } from "@/components/ExpiryDialog";
import { vaultCategory } from "@/components/funding/vaultCategory";

function Score({ c }) {
  const total = c.requirements.length;
  return (
    <div className="bg-slate-900 text-white rounded-lg p-5 flex flex-wrap items-center gap-6" data-testid="eligibility-score">
      <div><div className="text-4xl font-bold" data-testid="eligibility-pct">{c.pct}%</div><div className="text-xs text-slate-400">requirements met</div></div>
      <div className="text-sm"><b>{c.met}</b> of {total} met · <b>{c.verified}</b> verified with proof · {c.answered}/{total} answered</div>
      <div className="ml-auto flex flex-col items-end gap-2"><FundingReadyBadge ready={c.funding_ready} />
      <div className="text-sm font-semibold" data-testid="eligibility-verdict">{c.verdict === "ready" ? "Yes — you can open this" : c.verdict === "gaps" ? "Not yet — see your action plan" : "Answer every question to see your result"}</div></div>
    </div>
  );
}

export function EligibilityCheck() {
  const [form, setForm] = useState({ industry: "", state: "", city: "" });
  const [check, setCheck] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadDocs = useCallback(() => api.get("/documents").then((r) => setDocs(r.data)), []);
  useEffect(() => {
    api.get("/workspace").then((r) => { const p = r.data.profile || {}; setForm({ industry: p.industry || "", state: p.state || "", city: p.city || "" }); });
    api.get("/eligibility").then((r) => r.data[0] && setCheck(r.data[0]));
    loadDocs();
  }, [loadDocs]);
  const reload = useCallback(() => check && api.get(`/eligibility/${check.check_id}`).then((r) => setCheck(r.data)), [check]);
  usePoll(reload, check?.status === "pending", 3000);
  const start = async () => {
    setBusy(true);
    try { const r = await api.post("/eligibility", form); const g = await api.get(`/eligibility/${r.data.check_id}`); setCheck(g.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const answer = async (id, v) => { const r = await api.put(`/eligibility/${check.check_id}/answers`, { answers: { [id]: v } }); setCheck(r.data); };
  const upload = async (id, file) => {
    const req = check.requirements.find((x) => x.id === id);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", vaultCategory(req));
    try {
      const d = await api.post("/documents", fd);
      const r = await api.put(`/eligibility/${check.check_id}/proof`, { req_id: id, doc_id: d.data.doc_id });
      setCheck(r.data); loadDocs(); toast.success("Proof saved to your Document Vault"); if (EXPIRING.has(d.data.category)) setExpDoc(d.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const [scanReq, setScanReq] = useState(null);
  const [expDoc, setExpDoc] = useState(null);
  const scanned = async (doc) => {
    const id = scanReq.id;
    try {
      if (check.answers?.[id] !== "yes") await api.put(`/eligibility/${check.check_id}/answers`, { answers: { [id]: "yes" } });
      const r = await api.put(`/eligibility/${check.check_id}/proof`, { req_id: id, doc_id: doc.doc_id });
      setCheck(r.data); loadDocs(); toast.success("Scanned and ticked off your checklist"); if (EXPIRING.has(doc.category)) setExpDoc(doc);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const clearProof = async (id) => { const r = await api.put(`/eligibility/${check.check_id}/proof`, { req_id: id, doc_id: null }); setCheck(r.data); };
  const docName = (id) => docs.find((d) => d.doc_id === check.proofs?.[id])?.filename || (check.proofs?.[id] ? "document" : "");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="eligibility-form">
        <Input placeholder="Business type, e.g. Auto repair shop" value={form.industry} onChange={set("industry")} data-testid="eligibility-industry-input" />
        <Input placeholder="State, e.g. Texas" value={form.state} onChange={set("state")} data-testid="eligibility-state-input" />
        <Input placeholder="City (optional)" value={form.city} onChange={set("city")} data-testid="eligibility-city-input" />
        <Button onClick={start} disabled={busy || form.industry.length < 2 || form.state.length < 2} className="bg-emerald-600 hover:bg-emerald-700" data-testid="eligibility-start-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}Check requirements</Button>
      </div>
      {check?.status === "pending" && <p className="text-sm text-emerald-700" data-testid="eligibility-pending"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" />Researching federal, state and local requirements for {check.industry} in {check.state}… about 30–60 seconds.</p>}
      {check?.status === "error" && <p className="text-sm text-red-600" data-testid="eligibility-error">Something went wrong. Try again.</p>}
      {check?.status === "ready" && (
        <>
          <Score c={check} />
          <FundingShare check={check} onChange={setCheck} />
          <p className="text-sm text-slate-600">{check.summary}</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="eligibility-requirements">
            {check.requirements.map((r) => <RequirementRow key={r.id} r={r} answer={check.answers?.[r.id]} proofName={docName(r.id)} onAnswer={answer} onUpload={upload} onClearProof={clearProof} onScan={(id) => setScanReq(check.requirements.find((x) => x.id === id))} />)}
          </div>
          {check.verdict !== "pending" && <GapPlan check={check} />}
          <p className="text-xs text-slate-400">AI-researched from public sources. Requirements change, so confirm each item with the issuing agency before you rely on it.</p>
        </>
      )}
      {expDoc && <ExpiryDialog doc={expDoc} onClose={() => setExpDoc(null)} />}
      {scanReq && <ScanDialog open onClose={() => setScanReq(null)} fixedCategory={vaultCategory(scanReq)} defaultName={scanReq.proof || scanReq.title} title={`Scan: ${scanReq.proof || scanReq.title}`} onSaved={scanned} />}
    </div>
  );
}
